import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {object} LensPrescriptionRefs
 * @property {HTMLButtonElement} tabUpload - The "upload photo" tab button.
 * @property {HTMLButtonElement} tabManual - The "manual entry" tab button.
 * @property {HTMLElement} panelUpload - The upload panel.
 * @property {HTMLElement} panelManual - The manual entry panel.
 * @property {HTMLInputElement} fileInput - The prescription file input.
 * @property {HTMLInputElement[]} manualField - The manual prescription number inputs.
 * @property {HTMLElement} warning - The incomplete-prescription warning banner.
 *
 * @extends Component<LensPrescriptionRefs>
 */
class LensPrescriptionComponent extends Component {
  requiredRefs = ['panelUpload', 'panelManual', 'warning'];

  /** @type {AbortController | undefined} */
  #formListenerController;

  connectedCallback() {
    super.connectedCallback();

    const sectionTarget = this.closest('.shopify-section, dialog, product-card');
    sectionTarget?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);

    const form = this.#getForm();
    if (form) {
      this.#formListenerController = new AbortController();
      form.addEventListener('submit', this.#onFormSubmit, { signal: this.#formListenerController.signal });
    }

    this.#syncDisabledState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const sectionTarget = this.closest('.shopify-section, dialog, product-card');
    sectionTarget?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#formListenerController?.abort();
  }

  #getForm() {
    const formId = this.dataset.formId;
    if (!formId) return null;
    return /** @type {HTMLFormElement | null} */ (document.getElementById(formId));
  }

  /**
   * Toggles the whole block based on the newly selected variant's option values.
   * @param {CustomEvent & {selectedOptions?: Array<{name: string, value: string}>}} event
   */
  #onProductSelect = (event) => {
    const noRxValue = (this.dataset.noPrescriptionValue || '').trim().toLowerCase();
    const selectedOptions = event.selectedOptions || [];

    if (selectedOptions.length === 0) return;

    const hasNoPrescriptionOption = selectedOptions.some(
      (option) => option.value?.trim().toLowerCase() === noRxValue
    );

    this.hidden = hasNoPrescriptionOption;

    if (hasNoPrescriptionOption) {
      this.refs.warning.hidden = true;
    }
  };

  /**
   * Handles clicks on the tab buttons to switch between upload and manual entry.
   * @param {MouseEvent & {target: HTMLElement}} event
   */
  selectMethod(event) {
    const button = event.target.closest('[data-method]');
    if (!(button instanceof HTMLElement)) return;

    const method = button.dataset.method;
    const { tabUpload, tabManual, panelUpload, panelManual } = this.refs;

    const isUpload = method === 'upload';

    tabUpload.dataset.active = String(isUpload);
    tabUpload.setAttribute('aria-selected', String(isUpload));
    tabManual.dataset.active = String(!isUpload);
    tabManual.setAttribute('aria-selected', String(!isUpload));

    panelUpload.hidden = !isUpload;
    panelManual.hidden = isUpload;

    this.#syncDisabledState();
    this.#updateWarning();
  }

  handleFieldChange() {
    this.#updateWarning();
  }

  /**
   * Disables fields in the inactive panel so they're excluded from the cart form submission.
   */
  #syncDisabledState() {
    const { panelUpload, panelManual, fileInput, manualField } = this.refs;

    const uploadActive = !panelUpload.hidden;

    if (fileInput) fileInput.disabled = !uploadActive;

    const manualFields = Array.isArray(manualField) ? manualField : manualField ? [manualField] : [];
    for (const field of manualFields) {
      field.disabled = uploadActive;
    }
  }

  #isComplete() {
    const { panelUpload, fileInput, manualField } = this.refs;
    const uploadActive = !panelUpload.hidden;

    if (uploadActive) {
      return Boolean(fileInput?.files && fileInput.files.length > 0);
    }

    const manualFields = Array.isArray(manualField) ? manualField : manualField ? [manualField] : [];
    return manualFields.length > 0 && manualFields.every((field) => field.value.trim() !== '');
  }

  #updateWarning() {
    this.refs.warning.hidden = this.#isComplete();
  }

  /** @param {SubmitEvent} event */
  #onFormSubmit = (event) => {
    if (this.hidden) return;
    // Never block checkout — only surface a visual reminder.
    this.#updateWarning();
  };
}

if (!customElements.get('lens-prescription-component')) {
  customElements.define('lens-prescription-component', LensPrescriptionComponent);
}
